/**
 * Spaltenwahl der Sektion „Anfos & Umsatz" (P3-Review Punkt c, Spezifikation vorschlag_v3 §5).
 *
 * Jede Karte kennt ihre Spalten in drei Stufen: Pflicht (immer sichtbar, im Menü gesperrt), Standard (sichtbar,
 * bis der Nutzer etwas ändert; je Kartenbreite verschieden) und Wahl (nur im Menü). Gespeichert werden nur die
 * ABWEICHUNGEN vom Standard, je Browser — `{ v: 1, an, aus }`:
 *   sichtbar = Pflicht ∪ ((Standard der aktuellen Breite ∪ an) \ aus)
 * Ein „an" für eine Spalte, die an dieser Breite ohnehin Standard ist, wirkt also nichts, ein „aus" für eine
 * Spalte, die dort kein Standard ist, ebenfalls. Eine selbst gewählte Zusatzspalte erscheint damit auch am Handy.
 *
 * Die reinen Funktionen hier sind DOM-frei und getestet (`spaltenwahl.test.ts`); der Hook liest und schreibt
 * localStorage nur im try/catch — in privaten Fenstern wirft es, dann gilt einfach der Standard.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type Breite = 'schmal' | 'mittel' | 'breit';

/** Kartenbreite → Stufe. Gemessen wird die KARTE, nicht das Fenster (die offene Seitenleiste kostet ~350 px). */
export const breiteAus = (px: number): Breite => (px < 640 ? 'schmal' : px < 1000 ? 'mittel' : 'breit');

export interface Abweichung {
  v: 1;
  an: string[];
  aus: string[];
}

export const KEINE_ABWEICHUNG: Abweichung = { v: 1, an: [], aus: [] };

/** Gespeicherten Wert lesen; alles Unerwartete gilt als „keine Abweichung". */
export function abweichungAus(roh: string | null): Abweichung {
  if (!roh) return KEINE_ABWEICHUNG;
  try {
    const x = JSON.parse(roh) as Partial<Abweichung>;
    if (x?.v !== 1 || !Array.isArray(x.an) || !Array.isArray(x.aus)) return KEINE_ABWEICHUNG;
    return { v: 1, an: x.an.filter((k) => typeof k === 'string'), aus: x.aus.filter((k) => typeof k === 'string') };
  } catch {
    return KEINE_ABWEICHUNG;
  }
}

/**
 * Sichtbare Spalten in der Reihenfolge von `alle`. Unbekannte Schlüssel in der Abweichung (Spalte gibt es nicht
 * mehr) werden ignoriert; eine neue Standardspalte erscheint, weil sie in keinem „aus" steht.
 */
export function spaltenwahlZusammenfuehren(
  alle: readonly string[],
  pflicht: readonly string[],
  standard: readonly string[],
  abw: Abweichung,
): string[] {
  const an = new Set(abw.an);
  const aus = new Set(abw.aus);
  const std = new Set(standard);
  const pf = new Set(pflicht);
  return alle.filter((k) => pf.has(k) || ((std.has(k) || an.has(k)) && !aus.has(k)));
}

/**
 * Neue Abweichung nach dem Umschalten einer Spalte an der aktuellen Breite. Gespeichert wird gegen den Standard
 * DIESER Breite: Einschalten einer Standardspalte nimmt nur ein „aus" zurück, Einschalten einer Wahlspalte setzt
 * „an"; Ausschalten umgekehrt. Pflichtspalten sind nicht umschaltbar.
 */
export function umschalten(abw: Abweichung, key: string, an: boolean, standard: readonly string[], pflicht: readonly string[]): Abweichung {
  if (pflicht.includes(key)) return abw;
  const istStandard = standard.includes(key);
  const ohne = (l: string[]) => l.filter((k) => k !== key);
  if (an) {
    return { v: 1, an: istStandard ? ohne(abw.an) : [...ohne(abw.an), key], aus: ohne(abw.aus) };
  }
  return { v: 1, an: ohne(abw.an), aus: istStandard ? [...ohne(abw.aus), key] : ohne(abw.aus) };
}

/**
 * Kopplung `nurMit` (P2-V3 § 5): eine Spalte ist nur sichtbar, solange ihre Partnerspalte sichtbar ist — sonst fällt sie
 * heraus, auch wenn sie Standard oder gewählt ist. Ketten (A nur mit B, B nur mit C) werden bis zum Fixpunkt aufgelöst.
 * `nurMit`: Schlüssel → Partner. Reihenfolge bleibt die von `sichtbar`.
 */
export function nurMitAnwenden(sichtbar: readonly string[], nurMit: Readonly<Record<string, string>>): string[] {
  let aktuell = [...sichtbar];
  for (;;) {
    const da = new Set(aktuell);
    const naechste = aktuell.filter((k) => !nurMit[k] || da.has(nurMit[k]));
    if (naechste.length === aktuell.length) return naechste;
    aktuell = naechste;
  }
}

/** Weicht die Abweichung an DIESER Breite wirksam vom Standard ab? (steuert „angepasst" und „Zurücksetzen") */
export function istAngepasst(alle: readonly string[], pflicht: readonly string[], standard: readonly string[], abw: Abweichung): boolean {
  const mit = spaltenwahlZusammenfuehren(alle, pflicht, standard, abw);
  const ohne = spaltenwahlZusammenfuehren(alle, pflicht, standard, KEINE_ABWEICHUNG);
  return mit.length !== ohne.length || mit.some((k, i) => k !== ohne[i]);
}

const SPEICHER = 'auswertungen.spalten.';

const lese = (karte: string): Abweichung => {
  try {
    return abweichungAus(window.localStorage.getItem(SPEICHER + karte));
  } catch {
    return KEINE_ABWEICHUNG;
  }
};
const schreibe = (karte: string, abw: Abweichung | null) => {
  try {
    if (abw === null || (abw.an.length === 0 && abw.aus.length === 0)) window.localStorage.removeItem(SPEICHER + karte);
    else window.localStorage.setItem(SPEICHER + karte, JSON.stringify(abw));
  } catch {
    /* nur Komfort */
  }
};

/** Beschreibung einer Spalte für Menü und Wahl. */
export interface SpaltenMeta {
  key: string;
  label: string;
  tip?: string;
  /** Menügruppe („Menge", „Wert", …), Reihenfolge = Reihenfolge des ersten Auftretens. */
  gruppe: string;
  pflicht?: boolean;
  /** Nur sichtbar und wählbar, solange diese Partnerspalte sichtbar ist (`nurMitAnwenden`). */
  nurMit?: string;
}

export interface Spaltenwahl {
  /** Sichtbare Schlüssel in Definitionsreihenfolge. */
  sichtbar: string[];
  breite: Breite;
  angepasst: boolean;
  setze: (key: string, an: boolean) => void;
  zuruecksetzen: () => void;
  /** Ref an die Karte (oder ein Element in Kartenbreite) hängen — misst die Breite. */
  messRef: (el: HTMLElement | null) => void;
}

/**
 * Spaltenwahl einer Karte. `karte` ist der Speicherschlüssel; Karten mit derselben Spaltenliste (Kanal und Blog)
 * dürfen ihn teilen. `standards` je Breite; Pflichtspalten kommen aus `spalten`.
 */
export function useSpaltenwahl(karte: string, spalten: readonly SpaltenMeta[], standards: Record<Breite, readonly string[]>): Spaltenwahl {
  const [abw, setAbw] = useState<Abweichung>(() => lese(karte));
  const [breite, setBreite] = useState<Breite>('breit');
  const beobachter = useRef<ResizeObserver | null>(null);

  // Andere Karte mit demselben Schlüssel (Kanal/Blog) im selben Fenster: Änderungen gegenseitig übernehmen —
  // nur fremde, und den mitgeschickten Wert statt des Speichers: ohne Speicher (privates Fenster) läse die
  // Schwester sonst den leeren Stand und verwürfe ihre eigene Wahl (Codex-Review 23.10.).
  const ich = useRef(Symbol('spaltenwahl'));
  useEffect(() => {
    const neu = (e: Event) => {
      const d = (e as CustomEvent<{ von: symbol; abw: Abweichung }>).detail;
      if (d && d.von !== ich.current) setAbw(d.abw);
    };
    window.addEventListener(`spaltenwahl:${karte}`, neu);
    return () => window.removeEventListener(`spaltenwahl:${karte}`, neu);
  }, [karte]);

  const messRef = useCallback((el: HTMLElement | null) => {
    beobachter.current?.disconnect();
    beobachter.current = null;
    if (!el) return;
    setBreite(breiteAus(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((eintraege) => {
      const w = eintraege[0]?.contentRect.width;
      if (w) setBreite(breiteAus(w));
    });
    ro.observe(el);
    beobachter.current = ro;
  }, []);
  // Kein eigener Abbau-Effekt: React ruft die Ref beim Aushängen mit null auf (erster Zweig oben). Ein
  // Effekt-Cleanup trennte den Beobachter im StrictMode, ohne dass die Ref ihn neu anlegt (Codex-Review 23.10.).

  const alle = useMemo(() => spalten.map((s) => s.key), [spalten]);
  const pflicht = useMemo(() => spalten.filter((s) => s.pflicht).map((s) => s.key), [spalten]);
  const nurMit = useMemo(() => Object.fromEntries(spalten.filter((s) => s.nurMit).map((s) => [s.key, s.nurMit as string])), [spalten]);
  const standard = standards[breite];

  // Speichern, setzen, Schwesterkarte mit dem neuen Wert benachrichtigen.
  const aendere = useCallback(
    (f: (a: Abweichung) => Abweichung | null) => {
      const neu = f(abw) ?? KEINE_ABWEICHUNG;
      schreibe(karte, neu);
      setAbw(neu);
      window.dispatchEvent(new CustomEvent(`spaltenwahl:${karte}`, { detail: { von: ich.current, abw: neu } }));
    },
    [karte, abw],
  );

  return {
    // Ohne `nurMit` in den Spalten ist `nurMitAnwenden` die Identität — P3 bleibt unverändert.
    sichtbar: nurMitAnwenden(spaltenwahlZusammenfuehren(alle, pflicht, standard, abw), nurMit),
    breite,
    angepasst: istAngepasst(alle, pflicht, standard, abw),
    setze: (key, an) => aendere((a) => umschalten(a, key, an, standard, pflicht)),
    zuruecksetzen: () => aendere(() => null),
    messRef,
  };
}
